import styled from "@emotion/styled";
import { FC } from "react";
import { FieldInputProps } from "react-final-form";

import { Error } from "@livechat/design-system-icons";
import { DesignToken, Icon, RadiusToken, SpacingToken, Text } from "@livechat/design-system-react-components";

import {
  IRuleTriggerNewAgentMessage,
  IRuleTriggerNewMessage,
  IRuleTriggerSubject,
} from "store/entities/rules/interfaces";
import { textEllipsisStyles } from "styles/common";
import { withVar } from "themes/helpers";

import { TriggerFieldArrayElement } from "../../interfaces";

import { isTriggerType } from "./utils";

const MAX_PHRASES = 10;

interface IProps {
  input: FieldInputProps<TriggerFieldArrayElement>;
  onAccordionOpen: () => void;
}

export const TriggerMultilineLabel: FC<IProps> = ({ input, onAccordionOpen }) => {
  if (
    isTriggerType<IRuleTriggerSubject>(input, "subject") ||
    isTriggerType<IRuleTriggerNewMessage>(input, "newMessage") ||
    isTriggerType<IRuleTriggerNewAgentMessage>(input, "newAgentMessage")
  ) {
    const { containsIn, containsInCI, containsWholePhraseIn, containsWholePhraseInCI } = input.value;

    const phrases = containsIn || containsInCI || containsWholePhraseIn || containsWholePhraseInCI || [];

    if (phrases.length === 0) {
      return (
        <Phrase error onClick={onAccordionOpen}>
          <Icon size="small" source={Error} customColor={withVar(DesignToken.ContentBasicNegative)} />
          <Value size="sm">
            Enter {containsWholePhraseIn || containsWholePhraseInCI ? "phrase" : "string of characters"}…
          </Value>
        </Phrase>
      );
    }

    const phrasesToShow = phrases.slice(0, MAX_PHRASES);
    const remainingPhrases = phrases.slice(MAX_PHRASES);

    const morePhrasesTitle = remainingPhrases.join("\n");

    return (
      <PhraseContainer onClick={onAccordionOpen}>
        {phrasesToShow.map(phrase => (
          <Phrase key={phrase}>
            <Value>{phrase}</Value>
          </Phrase>
        ))}
        {remainingPhrases.length > 0 && (
          <MorePhrases bold title={morePhrasesTitle}>
            +{remainingPhrases.length}
          </MorePhrases>
        )}
      </PhraseContainer>
    );
  }

  return null;
};

const PhraseContainer = styled.div`
  margin-left: ${withVar(SpacingToken.Spacing7)};
  display: flex;
  flex-wrap: wrap;
  gap: ${withVar(SpacingToken.Spacing1)};
  cursor: pointer;
`;

const Value = styled(Text)`
  ${textEllipsisStyles}
`;

const MorePhrases = styled(Text)`
  display: flex;
  align-items: center;
  padding: ${withVar(SpacingToken.Spacing3)} ${withVar(SpacingToken.Spacing4)};
  cursor: default;
`;

const Phrase = styled.div<{ error?: boolean }>`
  display: flex;
  max-width: 100%;
  align-items: center;
  gap: ${withVar(SpacingToken.Spacing1)};
  cursor: pointer;
  border-radius: ${withVar(RadiusToken.Radius3)};
  background-color: ${({ error }): string =>
    error ? withVar(DesignToken.SurfaceAccentEmphasisLowNegative) : withVar(DesignToken.SurfaceTertiaryDefault)};
  padding: ${withVar(SpacingToken.Spacing3)} ${withVar(SpacingToken.Spacing4)};
  color: ${({ error }): string =>
    error ? withVar(DesignToken.ContentBasicNegative) : withVar(DesignToken.ContentBasicPrimary)};
`;
